import { motion } from 'framer-motion';
import {
  FolderIcon,
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@/config/icons';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableCategoryItem } from '../SortableCategoryItem';
import { Category } from '../SidebarDialogs';

interface CategoriesSectionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  categories: Category[];
  currentCategoryId: string | null;
  onCategoryClick: (categoryId: string) => void;
  onNewCategory: () => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (category: Category) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

export function CategoriesSection({
  open,
  onOpenChange,
  loading,
  categories,
  currentCategoryId,
  onCategoryClick,
  onNewCategory,
  onEditCategory,
  onDeleteCategory,
  onDragEnd,
}: CategoriesSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <div className="flex items-center justify-between">
        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
          <FolderIcon className="h-4 w-4" />
          <span>DOSSIERS</span>
          {open ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : (
            <ChevronDownIcon className="h-3 w-3" />
          )}
        </CollapsibleTrigger>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 rounded-full bg-muted hover:bg-muted/80"
          onClick={onNewCategory}
        >
          <AddIcon className="h-3 w-3" />
        </Button>
      </div>

      <CollapsibleContent className="mt-2 space-y-0.5">
        {loading ? (
          <motion.div
            className="space-y-1 px-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.2 }}
              >
                <Skeleton className="h-7 w-full rounded-lg" />
              </motion.div>
            ))}
          </motion.div>
        ) : categories.length === 0 ? (
          <p className="text-xs text-muted-foreground px-3 py-2">Aucun dossier</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={categories.map(c => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {categories.map((category) => (
                <SortableCategoryItem
                  key={category.id}
                  id={category.id}
                  name={category.name}
                  color={category.color}
                  ticketCount={category.ticket_count}
                  isActive={currentCategoryId === category.id}
                  onClick={() => onCategoryClick(category.id)}
                  onEdit={() => onEditCategory(category)}
                  onDelete={() => onDeleteCategory(category)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
